/**
 * Apply branding to DOM - DUY NHẤT 1 NƠI mutate DOM
 * Idempotent: can be called multiple times with same result
 *
 * Rules:
 * - Title: via resolveBrandAwareLoading (same as Loading Block)
 * - Favicon: set to branding.favicon_src + version, or remove if null
 * - OG tags: update if provided
 * - NO fallback Lovable icons
 */

import { resolveBrandAwareLoading } from '@/lib/branding/resolveBrandAwareLoading';
import type { BrandingNormalized } from './types';
import { DEFAULT_BRAND_TITLE } from './loadingBlockConstants';

/** Document title — aligned with Loading Block normalizer (fullscreen scope). */
function resolveDocumentTitle(branding: BrandingNormalized): string {
  const resolved = resolveBrandAwareLoading({
    scope: 'fullscreen',
    publicBranding: branding,
  });
  return resolved.title ?? DEFAULT_BRAND_TITLE;
}

/**
 * Apply branding to document (title, favicon, OG tags)
 * Idempotent: safe to call multiple times
 */
export const applyBrandingToDom = (branding: BrandingNormalized): void => {
  const documentTitle = resolveDocumentTitle(branding);

  if (import.meta.env.DEV) {
    console.debug('[applyBrandingToDom] Applying branding:', {
      site_title: branding.site_title,
      document_title: documentTitle,
      has_favicon: !!branding.favicon_src,
      has_logo: !!branding.logo_src,
      version: branding.version,
    });
  }

  // 1. Apply title (Loading Block SSOT)
  document.title = documentTitle;

  // 2. Apply favicon
  // Remove all existing favicon links
  const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
  existingFavicons.forEach((link) => link.remove());

  if (branding.favicon_src) {
    // Create new favicon link with cache-busting
    const favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/x-icon';
    const separator = branding.favicon_src.includes('?') ? '&' : '?';
    favicon.href = `${branding.favicon_src}${separator}v=${branding.version}`;
    document.head.appendChild(favicon);

    // Also add shortcut icon for older browsers
    const shortcutIcon = document.createElement('link');
    shortcutIcon.rel = 'shortcut icon';
    shortcutIcon.type = 'image/x-icon';
    shortcutIcon.href = favicon.href;
    document.head.appendChild(shortcutIcon);
  } else {
    // No favicon - create empty link to prevent browser from requesting default favicon
    const favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/x-icon';
    favicon.href = 'data:,';
    document.head.appendChild(favicon);
  }

  // 3. Apply OG tags (optional)
  const updateMetaTag = (property: string, content: string | null) => {
    if (!content) return;

    let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  if (branding.og_title) {
    updateMetaTag('og:title', branding.og_title);
  }
  if (branding.og_description) {
    updateMetaTag('og:description', branding.og_description);
  }
  if (branding.og_image_src) {
    const separator = branding.og_image_src.includes('?') ? '&' : '?';
    updateMetaTag('og:image', `${branding.og_image_src}${separator}v=${branding.version}`);
  }
};

